import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CircularProgress, Box, Typography, Alert } from '@mui/material';
import { isTokenExpired } from '../utils/jwt';
import { hasPermissionAccess, getRequiredPermissionsForRoute } from '../utils/permissionAccess';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermissions?: string[]; // Optional: override default route-based permission check
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredPermissions }) => {
  const { isAuthenticated, tokens, refreshTokens, isRefreshing, isInitializing, user } = useAuth();
  const location = useLocation();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    const checkAuthentication = async () => {
      // Wait for initial auth check to complete
      if (isInitializing) {
        return;
      }

      if (!tokens) {
        setIsCheckingAuth(false);
        return;
      }

      // Check if access token is expired
      if (isTokenExpired(tokens.accessToken)) {
        console.log('Access token expired, attempting refresh');
        const refreshSuccess = await refreshTokens();
        
        if (!refreshSuccess) {
          console.log('Token refresh failed, user will be redirected to login');
        }
      }

      // Always set checking to false after the check completes
      setIsCheckingAuth(false);
    };

    checkAuthentication();
  }, [tokens, refreshTokens, isInitializing]);

  // Show loading while initializing, checking authentication, or refreshing tokens
  if (isInitializing || isCheckingAuth || isRefreshing) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          {isRefreshing ? 'Refreshing session...' : 'Checking authentication...'}
        </Typography>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  // This handles cases where:
  // - No tokens exist in storage
  // - Tokens were cleared after failed refresh
  // - User is not authenticated for any reason
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check permission-based access control
  const requiredPerms = requiredPermissions || getRequiredPermissionsForRoute(location.pathname);
  const userPerms = user?.permissions || [];
  const hasAccess = hasPermissionAccess(user?.roleId, userPerms, requiredPerms);

  if (!hasAccess) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          gap: 2,
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body2">
            You don't have permission to access this page. Please contact your administrator if you believe this is an error.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // Render protected content
  return <>{children}</>;
};

export default ProtectedRoute; 